import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomBuyForMeCard from "./boxOrderCard";
import { useTranslation } from "react-i18next";
import CustomOrderInstructionCard from "../ordersForBuyForMe/customOrderInstructionCard";
import CustomBannerWithCheckAll from "./customBannerWithCheckAll";
import { updateProductItem } from "../../redux/reducer/productReducer";
import {
  ExclamationTriangleIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import RepackagingPopup from "./RepackagingPopup";
import { fetchMyCarts, updateCart } from "../../redux/reducer/cartReducer";
import GalleryPopup from "../sharedComponents/GalleryPopup";
import MoreDetailsPopup from "../ordersForBuyForMe/MoreDetailsPopup";

const CartPackaging = ({ carts }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectAllMap, setSelectAllMap] = useState({});
  const roundToTwo = (num) => parseFloat(Number(num).toFixed(2));
  const [show, setShow] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState({});
  const [selectedCartId, setSelectedCartId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showGalleryPopup, setShowGalleryPopup] = useState(null);
  const [showPackingPopup, setShowPackingPopup] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // Effect to hide warning after 3 seconds
  useEffect(() => {
    if (showWarning) {
      const timer = setTimeout(() => {
        setShowWarning(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showWarning]);

  const handleSelectAllToggle = (cartId, products, isChecked) => {
    setSelectAllMap((prev) => ({ ...prev, [cartId]: isChecked }));

    const productIds = products
      .filter((product) => product.status === "received")
      .map((product) => product._id);

    setSelectedProductIds((prev) => {
      const updated = { ...prev };
      if (isChecked) {
        updated[cartId] = productIds;
      } else {
        updated[cartId] = [];
      }
      return updated;
    });
  };

  const handleRepackageClick = (order) => {
    setSelectedOrder(order);
  };

  const closeModal = () => {
    setShow(false);
  };

  const handleRepackage = async (cartId) => {
    setLoading(true);
    const productIds = selectedProductIds[cartId] || [];

    try {
      for (const id of productIds) {
        await dispatch(
          updateProductItem({
            itemId: id,
            data: { isPacking: true },
          })
        ).unwrap();
      }

      await dispatch(
        updateCart({
          cartId,
          data: { isPacking: true, isReadyToPaid: false },
        })
      ).unwrap();
    } catch (err) {
      console.error("Error updating:", err);
    } finally {
      dispatch(fetchMyCarts());
      setLoading(false);
      setShow(false);
    }
  };

  const openShowGallery = (item) => {
    setShowGalleryPopup(item);
  };

  const handleConfirmPackaging = (cartId) => {
    if (
      Object.keys(selectedProductIds).length === 0 ||
      !selectedProductIds[cartId] ||
      selectedProductIds[cartId].length === 0
    ) {
      setShowWarning(true);
      return;
    }
    setShow(true);
    setSelectedCartId(cartId);
  };
  // console.log(Object.keys(selectedProductIds).length)
  return (
    <div>
      {carts.map((cart) => (
        <div key={cart._id} className="flex flex-col gap-4">
          <CustomBannerWithCheckAll
            orderNumber={cart.referenceCode}
            cartId={cart._id}
            products={cart.products}
            cartIsReadyToPaid={cart.isReadyToPaid}
            cartIsPacking={cart.isPacking}
            allChecked={selectAllMap[cart._id] || false}
            onSelectAllChange={handleSelectAllToggle}
          />

          <div className="grid w-full grid-cols-2 gap-2 mt-4 lg:gap-4 md:grid-cols-3">
            {cart.products && cart.products.length > 0 ? (
              cart.products.map((product) => (
                <CustomBuyForMeCard
                  key={product._id}
                  image={
                    product.img ||
                    "https://api.markaship.com/uploads/waiting.png"
                  }
                  title={product.name || "Product Title"}
                  size={product.additionalInfo || "N/A"}
                  link={product.productLink}
                  weight={product.weight}
                  count={product.count || "1"}
                  price={roundToTwo(Number(product.PackingFees)) || t("waiting")}
                  status={product.status}
                  isEditable={true}
                  isPacking={product.isPacking}
                  itemId={product._id}
                  onRepackage={() => handleRepackageClick(product)}
                  isChecked={selectAllMap[cart._id]}
                  onCheckboxChange={(value) => {
                    setSelectedProductIds((prev) => {
                      const prevIds = prev[cart._id] || [];
                      let updatedIds;
                      if (value) {
                        updatedIds = [...new Set([...prevIds, product._id])];
                      } else {
                        updatedIds = prevIds.filter((id) => id !== product._id);
                      }
                      return { ...prev, [cart._id]: updatedIds };
                    });
                  }}
                  cartIsPacking={cart.isPacking}
                  onShowGallery={() => openShowGallery(product.imgs)}
                  images={product.imgs}
                />
              ))
            ) : (
              <p>{t("NoProductsInCart")}</p>
            )}
          </div>

          <div
            className={`flex justify-between w-full gap-1 lg:gap-4 mt-4 ${
              cart.isPacking && "mb-5"
            }`}
          >
            {/* Area */}
            <div
              className={`flex flex-col ${cart.isPacking ? "w-1/3" : "w-1/2"}`}
            >
              <span className="mb-1 text-xs font-medium text-gray-500 lg:ms-2 lg:text-base">
                {t("Area")}
              </span>
              <div className="p-2 bg-white border lg:p-4">
                <span className="text-xs text-black lg:font-bold lg:text-lg">
                  {t(
                    `${
                      cart.area === "67dfef4a100f542a2c700c04"
                        ? "Turkey"
                        : "Europe"
                    }`
                  )}
                </span>
              </div>
            </div>
            {/* Status */}
            <div
              className={`flex flex-col  ${
                cart.isPacking ? "w-1/3" : "w-1/2"
              } `}
            >
              <span className="mb-1 text-xs font-medium text-gray-500 lg:ms-2 lg:text-base">
                {t("status")}
              </span>
              <div className="p-2 bg-white border lg:p-4">
                {cart.isPacking && !cart.isReadyToPaid ? (
                  <span className="text-xs text-black lg:font-bold lg:text-lg">
                    {t(`beingPacked`)}
                  </span>
                ) : cart.isPacking && cart.isReadyToPaid ? (
                  <span className="text-xs text-black lg:font-bold lg:text-lg">
                    {t(`Packaged`)}
                  </span>
                ) : (
                  <span className="text-xs text-black lg:font-bold lg:text-lg">
                    {t(`${cart.status}`)}
                  </span>
                )}
              </div>
            </div>

            {/* Total Weight After Packing */}
            {cart.isPacking && (
              <>
                <div
                  className={`flex flex-col  ${
                    cart.isPacking ? "w-1/3" : "w-1/2"
                  } `}
                >
                  <span className="mb-1 text-xs font-medium text-gray-500 lg:ms-2 lg:text-base">
                    {t("WeightAfterPackaging")}
                  </span>
                  <div className="p-2 bg-white border lg:p-4">
                    <span className="text-xs font-medium text-black lg:font-bold lg:text-lg">
                      {cart?.isReadyToPaid
                        ? `${cart?.totalWeightAfterPacking} ${t("kg")}`
                        : t("beingPacked")}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {!cart.isPacking && (
            <div className="flex justify-center gap-4 mb-5 lg:mb-10">
              {/* Confirm Button */}
              <div className="flex flex-col w-1/2">
                <button
                  onClick={() => handleConfirmPackaging(cart._id)}
                  className="z-0 inline-flex items-center justify-center h-full p-2 text-xs font-bold text-black transition duration-300 ease-in-out transform lg:p-4 lg:text-lg bg-custom-yellow rounded-custom md:text-sm hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color whitespace-nowrap "
                >
                  <span className="me-2">📦</span>
                  <span className="text-xs font-bold text-black lg:text-lg">
                    {t(`${"ConfirmPackaging"}`)}
                  </span>
                </button>

                {/* Warning Message */}
                {showWarning && (
                  <div className="flex items-center justify-center mt-2 text-orange-600">
                    <ExclamationTriangleIcon className="w-4 h-4 me-1" />
                    <span className="text-xs font-medium text-black lg:text-sm">
                      {t("PleaseSelectProducts")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {cart.isPacking && cart.isReadyToPaid && (
            <div className="flex justify-center gap-4 mb-5 lg:mb-10">
              {/* Confirm Button */}
              <div className="flex flex-col w-1/2">
                <button
                  onClick={() => {
                    setShowPackingPopup(true);
                  }}
                  className="z-0 inline-flex items-center justify-center h-full p-2 text-xs font-bold text-black transition duration-300 ease-in-out transform lg:p-4 lg:text-lg bg-custom-yellow rounded-custom md:text-sm hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color whitespace-nowrap "
                >
                  <CreditCardIcon className={`w-4 text-black me-2`} />
                  <span className="text-xs font-bold text-black lg:text-lg">
                    {t(`${"packaging_details"}`)}
                  </span>
                </button>
              </div>
            </div>
          )}

          {(cart?.status === "purchased" || cart?.status === "requested") && (
            <CustomOrderInstructionCard title={t("WaitingReceived")} />
          )}

          {show && Object.keys(selectedProductIds).length > 0 && (
            <RepackagingPopup
              closeModal={closeModal}
              handleRepackage={() => handleRepackage(selectedCartId)}
              isDisabled={selectedProductIds[selectedCartId]?.length === 0}
              loading={loading}
            />
          )}

          {showPackingPopup && (
            <MoreDetailsPopup
              referenceCode={cart.referenceCode}
              status={cart.status}
              cart={cart}
              imgCart={cart?.img}
              cartIsPacking={true}
              onClose={() => setShowPackingPopup(false)}
            />
          )}
        </div>
      ))}

      {showGalleryPopup && (
        <GalleryPopup
          images={showGalleryPopup}
          onClose={() => setShowGalleryPopup(null)}
        />
      )}
    </div>
  );
};

export default CartPackaging;
